/**
 * Which run holds the price steps (F16), read off the run directory the page already holds.
 *
 * The rule (`PriceRun` in `scope.ts`) needs one fact per candidate run — has it a price step? — and
 * the directory row states it: `price_actions`, the count of the run's rows in the price-action table
 * (bata-platform ADR 6176). It is the very figure the run's decision summary calls
 * `price_actions_total` (ADR 6036): both are `count(*)` over the same table for the same run, with no
 * filter on a decision or on supersession, so «has a price step» is the same fact whichever is asked.
 * It used to be asked of the summaries, newest run first and one at a time (audit 4): up to ten
 * sequential requests to learn what the directory already said. Now nothing is read:
 *
 *   * the run on screen is asked first; a price step on it settles the question;
 *   * only when it has none are the directory's other runs considered, in the directory's order
 *     (newest recorded first, the order the walk used), and the first with a price step wins — the
 *     newest run with a price step;
 *   * a run the directory page does not list is itself; a directory that failed is the run itself;
 *     nothing is decided before the directory has answered.
 *
 * Pure: no read of its own. The summary of the chosen run is read by its caller, once, for the
 * pending counts.
 */

import type { PriceRun } from './scope';

/** The directory's state, as much of it as the rule needs. */
export interface PriceRunDirectory {
  /** `true` once the directory answered. */
  readonly ready: boolean;
  /** `true` once it failed — the run is then itself. */
  readonly failed: boolean;
  /** Its runs, newest recorded first, each with the price steps its row counts. */
  readonly runs: readonly { readonly run_id: string; readonly price_actions: number }[];
}

export function resolvePriceRun(runId: string | null, directory: PriceRunDirectory): PriceRun | null {
  if (runId === null) return null;
  if (directory.failed) return { kind: 'same', runId };
  if (!directory.ready) return { kind: 'pending' };
  const own = directory.runs.find((run) => run.run_id === runId);
  if (own === undefined || own.price_actions > 0) return { kind: 'same', runId };
  const newest = directory.runs.find((run) => run.run_id !== runId && run.price_actions > 0);
  return newest === undefined ? { kind: 'same', runId } : { kind: 'moved', runId: newest.run_id, from: runId };
}
